// @ts-nocheck
import { useState, useEffect, ReactNode } from 'react';
import { UnstyledButton } from '@mantine/core';
import { Box, Center, Group, Stack, Text, Title } from '@/components/ui';
import { FadeIn } from '@/components/animations';

export interface Slide {
  title: string;
  description: string;
  image?: string;
  icon?: ReactNode;
  gradient?: { from: string; to: string };
}

interface CarouselSlidesProps {
  slides: Slide[];
  autoPlay?: boolean;
  interval?: number; // milliseconds
  showDots?: boolean;
  showNavButtons?: boolean;
}

export default function CarouselSlides({
  slides,
  autoPlay = true,
  interval = 5000,
  showDots = true,
  showNavButtons = false,
}: CarouselSlidesProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!autoPlay || isPaused || slides.length <= 1) return;

    const timer = setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, interval, isPaused, slides.length]);

  const goToSlide = (index: number) => {
    setActiveIndex(index);
  };

  const currentSlide = slides[activeIndex];

  return (
    <Box
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      style={{
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Slide Content */}
      <FadeIn key={activeIndex} duration={600}>
        <Box
          style={(theme) => ({
            height: '100%',
            padding: '3rem 2rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            background: currentSlide.gradient
              ? `linear-gradient(135deg, ${currentSlide.gradient.from}, ${currentSlide.gradient.to})`
              : currentSlide.image
              ? `url(${currentSlide.image})`
              : `linear-gradient(135deg, ${theme.colors.indigo[6]}, ${theme.colors.cyan[5]})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
          })}
        >
          {/* Overlay for better text readability */}
          <Box
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.3)',
            }}
          />

          <Stack gap="xl" align="center" style={{ position: 'relative', zIndex: 1, maxWidth: 500 }}>
            {/* Icon or Image */}
            {currentSlide.icon && (
              <Center
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                }}
              >
                {currentSlide.icon}
              </Center>
            )}

            {/* Title */}
            <Title order={2} c="white" fw={700}>
              {currentSlide.title}
            </Title>

            {/* Description */}
            <Text size="lg" c="white" style={{ opacity: 0.95 }}>
              {currentSlide.description}
            </Text>
          </Stack>
        </Box>
      </FadeIn>

      {/* Navigation Dots */}
      {showDots && slides.length > 1 && (
        <Center
          style={{
            position: 'absolute',
            bottom: 30,
            left: 0,
            right: 0,
            zIndex: 2,
          }}
        >
          <Group gap="sm">
            {slides.map((_, index) => (
              <UnstyledButton
                key={index}
                onClick={() => goToSlide(index)}
                style={{
                  width: activeIndex === index ? 32 : 12,
                  height: 12,
                  borderRadius: 6,
                  backgroundColor: activeIndex === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </Group>
        </Center>
      )}
    </Box>
  );
}
